import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  BarChart2,
  AlertCircle,
  ArrowLeft,
  CheckCircle2
} from 'lucide-react';

const SurveyAssessment = ({ 
  assessment,
  onSubmit,
  onCancel
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);

  // Ensure assessment and questions exist
  if (!assessment || !assessment.questions) {
    console.error('Missing assessment or questions:', assessment);
    return <div>Loading...</div>;
  }

  console.log('Assessment data:', {
    assessment,
    currentQuestion: assessment.questions[currentQuestionIndex],
    totalQuestions: assessment.questions.length
  });

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / assessment.questions.length) * 100;

  const handleNext = () => {
    if (currentQuestionIndex < assessment.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setShowSubmitAlert(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleAnswer = (value) => {
    console.log('Saving answer:', {
      questionId: currentQuestion.id,
      value
    });
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleSubmitConfirm = () => {
    onSubmit(answers);
  };

  const isAnswered = (questionId) => {
    return answers[questionId] !== undefined;
  };

  const renderQuestionInput = () => {
    if (!currentQuestion) {
      console.error('No current question available');
      return <div>Loading question...</div>;
    }

    console.log('Rendering question:', {
      id: currentQuestion.id,
      text: currentQuestion.text,
      type: currentQuestion.type,
      options: currentQuestion.options
    });

    // For debugging
    if (!currentQuestion.options || currentQuestion.options.length === 0) {
      console.warn('Question has no options:', currentQuestion);
      return (
        <div className="flex items-center p-4 bg-yellow-50 text-yellow-700 rounded-xl">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>This question has no options</span>
        </div>
      );
    }

    const renderOption = (option, index, isSelected) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
      >
        <button
          onClick={() => handleAnswer(option)}
          className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-200 group
            ${isSelected 
              ? 'border-primary-600 bg-primary-50 text-primary-900' 
              : 'border-gray-100 dark:border-gray-700 hover:border-primary-100 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-base">
              {option}
            </span>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                <CheckCircle2 className="w-5 h-5 text-primary-600" />
              </motion.div>
            )}
          </div>
        </button>
      </motion.div>
    );

    // For all single-selection questions (including likert)
    if (['likert', 'single-selection', 'dropdown-single-selection'].includes(currentQuestion.type.toLowerCase())) {
      return (
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => 
            renderOption(
              option,
              index,
              answers[currentQuestion.id] === option
            )
          )}
        </div>
      );
    }

    // For multiple selection questions
    if (['multiple-selection', 'multiple_choice', 'checkbox'].includes(currentQuestion.type.toLowerCase())) {
      return (
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const isSelected = (answers[currentQuestion.id] || []).includes(option);
            return renderOption(
              option,
              index,
              isSelected,
              () => {
                const currentAnswers = answers[currentQuestion.id] || [];
                const newAnswers = isSelected
                  ? currentAnswers.filter(a => a !== option)
                  : [...currentAnswers, option];
                handleAnswer(newAnswers);
              }
            );
          })}
        </div>
      );
    }

    console.warn(`Unsupported question type: ${currentQuestion.type}`, currentQuestion);
    return (
      <div className="flex items-center p-4 bg-red-50 text-red-700 rounded-xl">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span>Unsupported question type: {currentQuestion.type}</span>
      </div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      <div className="mb-8">
        <button
          onClick={onCancel}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>Back to Dashboard</span>
        </button>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="mb-8">
          <motion.div 
            variants={itemVariants}
            className="flex items-center justify-between"
          >
            <h1 className="text-2xl font-bold text-gray-900">
              {assessment.title || 'Survey Assessment'}
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <BarChart2 className="w-4 h-4" />
              <span>Question {currentQuestionIndex + 1} of {assessment.questions.length}</span>
            </div>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="mt-2"
          >
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="h-2 bg-primary-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={itemVariants}
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        >
          <div className="p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="text-xl font-medium text-gray-900 mb-6">
                  {currentQuestion.text}
                </h2>
                <div className="debug-info">
                  <pre>{JSON.stringify(currentQuestion, null, 2)}</pre>
                </div>
                {renderQuestionInput()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors
                  ${currentQuestionIndex === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </button>
              <button
                onClick={handleNext}
                disabled={!isAnswered(currentQuestion.id)}
                className={`inline-flex items-center px-6 py-3 rounded-lg transition-all 
                  ${isAnswered(currentQuestion.id)
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {currentQuestionIndex === assessment.questions.length - 1 ? 'Submit' : 'Next'}
                {currentQuestionIndex < assessment.questions.length - 1 && (
                  <ChevronRight className="w-5 h-5 ml-1" />
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {showSubmitAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Submit Assessment
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to submit this assessment? You won't be able to change your answers after submission.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowSubmitAlert(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Review Answers
                </button>
                <button
                  onClick={handleSubmitConfirm}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Confirm Submit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SurveyAssessment;